import { titleCase } from '../titleCase.ts';
import { PERMIT_KEY } from './gates.ts';
import { ProcessTreeDocSchema, type ProcessNodeDefT, type ProcessTreeDoc, type TaskOwner } from './schema.ts';

/**
 * A project's own settings on a step of the master process tree (Jason, 2026-10-07: "in that project, I should be able
 * to make all the same kind of edits, whether it needs permits, inspections, pre-install, design review, estimate ...
 * adjustments to this tree will be applied to nodes in projects unless they've been overridden at the project level";
 * migration 0057). A job reads the master tree as last saved with its own settings over it (`withOverrides`), so a
 * saved change to the master reaches every project's steps except a setting the project has made its own.
 */
export const OVERRIDE_FIELDS = ['permit', 'inspection', 'preInstall', 'deliversReview', 'deliversEstimate'] as const;
export type OverrideField = (typeof OVERRIDE_FIELDS)[number];

/**
 * A project's own settings, by the step's key: each one it has made its own (the master's otherwise): the switches, and
 * what the client reads of it (design study 25, q-what B: `clientNote`, empty when the client sees its tasks only).
 */
export type NodeOverride = Partial<Record<OverrideField, boolean>> & { clientNote?: string };
export type NodeOverrides = Record<string, NodeOverride>;

/** What a step has when a setting is on, in words, as the master tree's card says it. */
export const OVERRIDE_WORDS: Record<OverrideField, string> = {
  permit: 'needs a permit',
  inspection: 'needs an inspection',
  preInstall: 'needs a pre-install inspection',
  deliversReview: 'delivers a design review',
  deliversEstimate: 'delivers an estimate',
};

/** The settings a step of the tree can have on a project: the switches the master tree's card gives its kind. */
export function overridableFields(n: ProcessNodeDefT): OverrideField[] {
  if (n.parent !== undefined) return [];
  if (n.kind === 'work') return OVERRIDE_FIELDS.filter((f) => f !== 'permit' || n.key !== PERMIT_KEY);
  if (n.kind === 'gate') return ['deliversEstimate'];
  return [];
}

/** A setting made or given back, in words for the job's draft and its timeline. */
export function overrideWords(title: string, field: OverrideField, value: boolean | null): string {
  const name = `“${titleCase(title)}”`;
  if (value === null) return `${name} follows the master tree again on whether it ${OVERRIDE_WORDS[field]}`;
  return value ? `${name} ${OVERRIDE_WORDS[field]} on this job` : `${name} no longer ${OVERRIDE_WORDS[field]} on this job`;
}

/** An inspection in a step's list, the inspector's (the authority) or NSB's check before it goes in. */
const isHold = (n: ProcessNodeDefT, parent: string, owner: TaskOwner) => n.parent === parent && n.kind === 'inspection' && n.owner === owner;

/**
 * The master tree with a project's own settings over it. A setting the step's kind no longer has (the master made it
 * another kind since) is passed over. The holds are the step's own list, as saving the master makes them (core
 * `lockIn`): an inspection is the authority's Inspection last in its list, a pre-install inspection NSB's Site checked
 * ready first, each round what the step does (The work, where it lists nothing); a permit is the step coming after
 * Permits in hand.
 */
export function withOverrides(doc: ProcessTreeDoc, overrides: NodeOverrides): ProcessTreeDoc {
  const keys = Object.keys(overrides).filter((k) => Object.keys(overrides[k] ?? {}).length);
  if (!keys.length) return doc;
  let nodes: ProcessNodeDefT[] = doc.nodes.map((n) => ({ ...n, ...(n.before ? { before: [...n.before] } : {}) }));
  const taken = new Set(nodes.map((n) => n.key));
  const fresh = (base: string) => {
    let k = base;
    for (let i = 2; taken.has(k); i++) k = `${base}-${i}`;
    taken.add(k);
    return k;
  };
  const strip = (gone: Set<string>) => {
    nodes = nodes.filter((n) => !gone.has(n.key)).map((n) => (n.before?.some((b) => gone.has(b)) ? { ...n, before: n.before.filter((b) => !gone.has(b)) } : n));
  };
  for (const key of keys) {
    const was = nodes.find((n) => n.key === key);
    if (!was) continue;
    const set = overrides[key]!;
    // What the client reads of it, the project's own: an empty one, its tasks only, as the master writes none.
    if (set.clientNote !== undefined && was.parent === undefined) {
      const { clientNote: _master, ...rest } = was;
      const own: ProcessNodeDefT = set.clientNote.trim() ? { ...rest, clientNote: set.clientNote.trim() } : rest;
      nodes = nodes.map((n) => (n.key === key ? own : n));
    }
    const allowed = overridableFields(was).filter((f) => set[f] !== undefined);
    if (!allowed.length) continue;
    const next: ProcessNodeDefT = { ...nodes.find((n) => n.key === key)! };
    for (const f of allowed) {
      if (set[f]) next[f] = true;
      else delete next[f];
    }
    nodes = nodes.map((n) => (n.key === key ? next : n));
    const task = (title: string, kind: ProcessNodeDefT['kind'], owner: TaskOwner, base: string): ProcessNodeDefT => ({
      key: fresh(`${key}.${base}`),
      title,
      kind,
      parent: key,
      basic: ['client', 'internal'],
      ...(next.phase ? { phase: next.phase } : {}),
      owner,
    });
    /** What the step does, for the holds to sit round: its list but the inspections, or The work where it lists nothing. */
    const doing = (): ProcessNodeDefT[] => {
      const own = nodes.filter((c) => c.parent === key && c.kind !== 'inspection');
      if (own.length) return own;
      const work = task('The work', 'work', next.owner ?? 'nsb', 'work');
      nodes.splice(nodes.findIndex((n) => n.key === key) + 1, 0, work);
      return [work];
    };
    /** After the last of the step's list, in the tree's order. */
    const endOfList = () => {
      let at = nodes.findIndex((n) => n.key === key);
      for (let i = at + 1; i < nodes.length && nodes[i]!.parent === key; i++) at = i;
      return at + 1;
    };
    if (allowed.includes('preInstall')) {
      const have = nodes.filter((n) => isHold(n, key, 'nsb'));
      if (next.preInstall && !have.length) {
        const check = task('Site checked ready', 'inspection', 'nsb', 'pre-install');
        check.before = doing().map((c) => c.key);
        nodes.splice(nodes.findIndex((n) => n.key === key) + 1, 0, check);
      } else if (!next.preInstall && have.length) strip(new Set(have.map((n) => n.key)));
    }
    if (allowed.includes('inspection')) {
      const have = nodes.filter((n) => isHold(n, key, 'authority'));
      if (next.inspection && !have.length) {
        const work = doing();
        const inspection = task('Inspection', 'inspection', 'authority', 'inspection');
        const at = endOfList();
        nodes = nodes.map((n) => (work.some((w) => w.key === n.key) ? { ...n, before: [...new Set([...(n.before ?? []), inspection.key])] } : n));
        nodes.splice(at, 0, inspection);
      } else if (!next.inspection && have.length) strip(new Set(have.map((n) => n.key)));
    }
    if (allowed.includes('permit')) {
      const permits = nodes.find((n) => n.key === PERMIT_KEY);
      if (permits) {
        const comes = (permits.before ?? []).includes(key);
        if (next.permit && !comes) nodes = nodes.map((n) => (n.key === PERMIT_KEY ? { ...n, before: [...(n.before ?? []), key] } : n));
        // Only the wait the master's switch made: a step drawn after Permits in hand by hand keeps it.
        else if (!next.permit && comes && was.permit) nodes = nodes.map((n) => (n.key === PERMIT_KEY ? { ...n, before: (n.before ?? []).filter((b) => b !== key) } : n));
      }
    }
  }
  return ProcessTreeDocSchema.parse({ ...doc, nodes });
}
